import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api";
import { toast } from "sonner";

export default function Register() {
  const { register, user, loading } = useAuth();
  const [f, setF] = useState({ email: "", password: "", name: "", age: "", gender: "Male", heightCm: "", weightKg: "", unitsPref: "metric" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  if (!loading && user) {
    const needsOnboarding = user.goal == null || user.daysPerWeek == null || user.age == null;
    return <Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />;
  }

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (Number(f.age) < 18) { setErr("You must be 18 or older."); return; }
    setSaving(true);
    try {
      const payload = { email: f.email, password: f.password, name: f.name, age: Number(f.age), gender: f.gender, heightCm: Number(f.heightCm), unitsPref: f.unitsPref };
      if (f.weightKg) payload.weightKg = Number(f.weightKg);
      await register(payload);
      toast.success("Account created.");
      nav("/onboarding");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-6 pt-14 pb-8">
      <div className="overline">Create account · 01/04</div>
      <h1 className="font-display text-4xl uppercase mt-3">Who lifts?</h1>
      <p className="text-t2 text-sm mt-3">Only age, gender and height are required. Everything else can wait.</p>
      <form className="mt-8 space-y-4" onSubmit={submit}>
        <input className="input" placeholder="Full name" value={f.name} onChange={set("name")} required data-testid="reg-name" />
        <input className="input" placeholder="Email" type="email" value={f.email} onChange={set("email")} required data-testid="reg-email" />
        <input className="input" placeholder="Password (min 6 chars)" type="password" minLength={6} value={f.password} onChange={set("password")} required data-testid="reg-password" />
        <div className="grid grid-cols-2 gap-3">
          <input className="input" placeholder="Age (18+)" type="number" min={18} value={f.age} onChange={set("age")} required data-testid="reg-age" />
          <div className="flex gap-2">
            {["Male", "Female"].map((g) => (
              <button type="button" key={g} className={`chip flex-1 ${f.gender === g ? "selected" : ""}`} onClick={() => setF({ ...f, gender: g })} data-testid={`reg-gender-${g}`}>{g}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input className="input" placeholder={f.unitsPref === "metric" ? "Height (cm)" : "Height (in)"} type="number" step="0.1" value={f.heightCm} onChange={set("heightCm")} required data-testid="reg-height" />
          <input className="input" placeholder={f.unitsPref === "metric" ? "Weight (kg, optional)" : "Weight (lb, optional)"} type="number" step="0.1" value={f.weightKg} onChange={set("weightKg")} data-testid="reg-weight" />
        </div>
        <div className="flex gap-2">
          {[{v:"metric",l:"Metric"},{v:"imperial",l:"Imperial"}].map((o) => (
            <button type="button" key={o.v} className={`chip flex-1 ${f.unitsPref === o.v ? "selected" : ""}`} onClick={() => setF({ ...f, unitsPref: o.v })} data-testid={`reg-units-${o.v}`}>{o.l}</button>
          ))}
        </div>
        {err && <div className="text-red text-sm" data-testid="reg-error">{err}</div>}
        <button className="btn btn-primary w-full" disabled={saving} data-testid="reg-submit">{saving ? "…" : "Continue"}</button>
      </form>
      <div className="mt-6 text-center text-sm text-t2">Have an account? <Link to="/login" className="text-lime">Sign in</Link></div>
    </div>
  );
}
